import {
  DateRangeType,
  DisplayContext as DateRangeDisplayContext,
  SpecialTimeUnit,
} from 'common/widgets/date-range-picker/models/types';
import {PropertyType, ResourceType} from 'common/report/constants';
import {Segmentation} from 'common/types/reports/query';
import {findKeyByValue} from 'common/util/typescript';
import {DateRangeFactory} from 'common/widgets/date-range-picker/models/date-range-factory';
import type {SavedCohortSegment} from 'common/query-builder/query-block/segment-menu/types';

import {CohortsPropertyFilter} from './cohorts-property-filter';
import {DefaultPropertyFilter} from './default-property-filter';
import {PropertyFilter} from './property-filter';
import {CustomEventPropertyFilter} from './segfilter';
import {DisplayContext, NthTimeOperator, StringOperator} from './types';
import {BooleanOperator} from './types';

export {STRING_TYPECAST_PROPERTIES} from './../../../report/constants';

export function createPropertyFilter(
  attributes: Partial<PropertyFilter>,
  {displayContext}: {displayContext: DisplayContext},
): PropertyFilter {
  const PropertyFilterClass = {
    [DisplayContext.Cohorts]: CohortsPropertyFilter,
    [DisplayContext.CustomEvent]: CustomEventPropertyFilter,
    [DisplayContext.Default]: DefaultPropertyFilter,
    [DisplayContext.Insights]: DefaultPropertyFilter,
    [DisplayContext.Signal]: PropertyFilter,
  }[displayContext];
  return new PropertyFilterClass(attributes);
}

export const DATE_RANGE_DISPLAY_CONTEXT_MAP: {[k in DisplayContext]: DateRangeDisplayContext} = {
  [DisplayContext.Cohorts]: DateRangeDisplayContext.Default,
  [DisplayContext.CustomEvent]: DateRangeDisplayContext.PropertyFilter,
  [DisplayContext.Insights]: DateRangeDisplayContext.PropertyFilter,
  [DisplayContext.Default]: DateRangeDisplayContext.PropertyFilter,
  [DisplayContext.Signal]: DateRangeDisplayContext.Default,
};

export class PropertyFilterFactory {
  public static createPropertyFilter(
    attributes: Partial<PropertyFilter>,
    {displayContext}: {displayContext: DisplayContext},
  ): PropertyFilter {
    return createPropertyFilter(attributes, {displayContext});
  }

  public static createFromSegment(
    segment: Partial<PropertyFilter>,
    {displayContext}: {displayContext: DisplayContext},
  ) {
    let event = {...segment};
    if ([SpecialTimeUnit.DayOfWeek, SpecialTimeUnit.HourOfDay].includes(event[`unit`])) {
      event = {
        ...event,
        filterDateUnit: event[`unit`],
        propertyType: PropertyType.String,
        filterOperator: StringOperator.Equals,
        propertyDefaultType: PropertyType.Datetime,
      };
    } else if (event.propertyType === PropertyType.Datetime && !event.filterValue) {
      event.filterOperator = DateRangeType.Between;
      event.filterValue = DateRangeFactory.createDateRange(
        {type: DateRangeType.Between},
        {displayContext: DateRangeDisplayContext.RelativeRange},
      );
    }
    // TODO remove this once breakdowns and lists use the same format
    if (event.propertyType === PropertyType.List) {
      event = {
        ...event,
        joinPropertyType: PropertyType.List,
        propertyType: PropertyType.String,
      };
    }

    if ((event as Segmentation).listItemGroup) {
      event.listItemFilters = [
        PropertyFilterFactory.createPropertyFilter((event as Segmentation).listItemGroup, {displayContext}),
      ];
      event.filterOperator = BooleanOperator.True;
      event.filterValue = true;
      delete (event as Segmentation).listItemGroup;
    }

    return PropertyFilterFactory.createPropertyFilter(event, {displayContext});
  }
}

/**
 * Creates a first time in period filter
 * @param displayContext DisplayContext
 */
export function createFirstTimeInPeriodEventFilter({
  displayContext = DisplayContext.Default,
}: {
  displayContext?: DisplayContext;
} = {}) {
  return createPropertyFilter(
    {
      propertyType: PropertyType.NonProperty,
      propertyDefaultType: PropertyType.NonProperty,
      filterOperator: NthTimeOperator.FirstTimeInPeriod,
      resourceType: ResourceType.Event,
      propertyName: `$first_time_event`,
    },
    {displayContext},
  );
}

/**
 * Creates a last time in period filter
 * @param displayContext DisplayContext
 */
export function createLastTimeInPeriodEventFilter({
  displayContext = DisplayContext.Default,
}: {
  displayContext?: DisplayContext;
} = {}): PropertyFilter {
  return createPropertyFilter(
    {
      propertyType: PropertyType.NonProperty,
      propertyDefaultType: PropertyType.NonProperty,
      filterOperator: NthTimeOperator.LastTimeInPeriod,
      resourceType: ResourceType.Event,
      propertyName: `$last_time_event`,
    },
    {displayContext},
  );
}

/**
 * Creates a default first time filter
 * @param displayContext DisplayContext
 */
export function createFirstTimeEventFilter({
  displayContext = DisplayContext.Default,
}: {
  displayContext?: DisplayContext;
} = {}): PropertyFilter {
  return createPropertyFilter(
    {
      propertyType: PropertyType.NonProperty,
      propertyDefaultType: PropertyType.NonProperty,
      filterOperator: NthTimeOperator.NthTime,
      resourceType: ResourceType.Event,
      propertyName: `$nth_time_event`,
    },
    {displayContext},
  );
}

export function isFirstTimeEventFilter(filter: PropertyFilter) {
  return (
    filter &&
    filter.resourceType === ResourceType.Event &&
    Boolean(findKeyByValue(NthTimeOperator, filter.filterOperator))
  );
}

export function isLookupPropertyFilter(filter) {
  return !!filter.dataGroupId && (Array.isArray(filter.property) || Array.isArray(filter.propertyName));
}

export function isEventFilter(filter: PropertyFilter) {
  return filter.resourceType === ResourceType.Event;
}

export function isUserFilter(filter: PropertyFilter) {
  return filter.resourceType === ResourceType.User;
}

export function isCohortFilter(filter: PropertyFilter) {
  return filter.resourceType === ResourceType.Cohort;
}

export function isBehaviorFilter(filter: PropertyFilter) {
  return !!filter.behavior;
}

/** Defines if a Cohort filter includes a given cohort id */
export function cohortFilterIncludesId(filter: PropertyFilter, cohortId: number) {
  return (
    isCohortFilter(filter) &&
    Array.isArray(filter.filterValue) &&
    (filter.filterValue as SavedCohortSegment[]).some((filterValue) => filterValue.cohort.id === cohortId)
  );
}
